import { formatDate, formatNumber } from '@angular/common';
import { MathHlp } from 'src/lib/fliko/helpers/math.helper';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { ReplaceMap } from 'src/lib/fliko/interfaces/replaceMap';
import { ConciergeType } from 'src/lib/fliko/models/concierge/concierge.data';
import { IssueStatus, IssueType, Payer } from 'src/lib/fliko/models/defects/issue.base';
import { IssueFieldChangeKey } from 'src/lib/fliko/models/defects/issue-history.data';
import { DefectType } from 'src/lib/fliko/models/defects/defect-settings.data';
import { Address } from 'src/lib/fliko/models/person/address.data';
import { Language, LocalisedType } from 'src/lib/fliko/models/translation/generic';
import { dTime, tTime } from 'src/lib/fliko/types/time.type';
import { DefectLocationCommon } from 'src/app/view/defects/service/models/defect-view-models';
import { LocLabel, LocLabelModifier } from '../components/clickable-row-table/models/data-table-config.models';
import { LocalizationService } from '../services/localization.service';
import { GrammarHelper } from './grammar.helper';

// prettier-ignore
export type DateFormat =
  | 'dShort'        // dd.MM.yyyy
  | 'dMed'          // dd MMM yyyy
  | 'dLong'         // dd MMMM yyyy
  | 'dLongDay'      // EEEE, dd MMMM yyyy
  | 'dMonthLong'    // MMMM
  | 'dMonthMed'     // MMM
  | 'dShortNoYear'  // dd.MM
  | 'dMedNoYear'    // dd MMM
  | 'dLongNoDay'    // LLLL yyyy
  | 'tShort'        // HH:mm
  | 'tLong'         // HH:mm:ss
  | 'dtShort'       // dd.MM.yyyy HH:mm
  | 'dtShortNoYear' // dd.MM HH:mm
  | 'dtMed'         // dd MMM YYYY, HH:mm
  | 'dtMedNoYear'   // dd MMM, HH:mm
  | 'dtLong'        // dd MMMM yyyy, HH:mm
  | 'file'          // dd-MM-yyyy_HH-mm-ss-SSSS
  | 'dISO'          // yyyy-MM-dd
  ;
// prettier-ignore
export type NumberFormat =
  | 'twoDecConst' // 1.2
  | 'twoDec'      // 1.0-2
  | 'oneDec'      // 1.0-1
  | 'noDec'       // 1.0
  ;

/**
 * LocalizationHelper with required LocalizationService
 */
export class LocalizationHelper {
  constructor(private ls: LocalizationService) {}

  // #region Misc

  /**
   * Translate conditional keys
   * @param {boolean}    value       condition determining suffix
   * @param {string}     prefix      first half of translation key
   * @param {string}     trueSuffix  second half of translation key - used if value == true
   * @param {string}     falseSuffix second half of translation key - used if value == false
   * @param {ReplaceMap} replaceMap  optional replace map
   * @return {string} translated prefix.suffix key
   */
  public ifElse(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, replaceMap?: ReplaceMap): string {
    const key = `${prefix}.${value ? trueSuffix : falseSuffix}`;

    return replaceMap ? this.ls.trReplace(key, replaceMap) : this.ls.translate(key);
  }

  /**
   * Most trivial case of LocalizationHelper.ifElse
   * @param {boolean} value
   * @return translated 'label.yes' or 'label.no'
   */
  public yesNo(value: boolean): string {
    return this.ifElse(value, 'label', 'yes', 'no');
  }

  /**
   * Translation for localized name with modifiers
   * @param {LocLabel} name
   * @return {string}
   */
  public locLabel(name: LocLabel): string {
    let ret = name.key;
    if (name.replaceMap) ret = this.ls.trReplace(name.key, name.replaceMap);
    else ret = this.ls.translate(name.key);

    if (name.modifier) {
      switch (name.modifier) {
        case LocLabelModifier.CAP:
          return StrHlp.capitalize(ret);
        case LocLabelModifier.LOWER:
          return ret.toLocaleLowerCase();
        case LocLabelModifier.UPPER:
          return ret.toLocaleUpperCase();
      }
    }

    return ret;
  }

  /**
   * Localize title field from any object contains title (used for SelectInputHelper and other unnamed types)
   * @param array
   * @return localized title field
   */
  public localizeTitle<T extends { title: string }>(array: T[]): T[] {
    return array.map((x) => {
      return { ...x, title: StrHlp.capitalize(this.ls.translate(x.title)) };
    });
  }

  /**
   * Translate Localized type
   * @param type
   * @return
   */
  public localizeType(type: LocalisedType): string {
    return this.ls.getData(type.label) ?? type.id;
  }

  // #endregion

  // #region Address
  /**
   * @param address
   * @param line         0 for single-line address, 1 or 2 for specific line
   * @param streetPrefix determines if use 'ul.' symbol before street number
   * @return
   */
  public addressToString(address: Address | null | undefined, line = 0, streetPrefix = false): string {
    if (address) {
      const street =
        streetPrefix && address.street ? this.ls.trReplace('addr.streetName', { $name: address.street }) : address.street;
      const line1 = `${street} ${address.number}`.trim();
      const line2 = `${address.zipCode} ${address.city}${address.country ? ', ' + address.country : ''}`.trim();

      switch (line) {
        case 0:
          return line1 && line2 ? `${line1}, ${line2}` : line1 || line2;
        case 1:
          return line1;
        case 2:
          return line2;
        default:
          return '';
      }
    } else return '';
  }
  // #endregion

  // #region Date

  // prettier-ignore
  private readonly weekdaysLong = ['day.monday', 'day.tuesday', 'day.wednesday', 'day.thursday',
    'day.friday', 'day.saturday', 'day.sunday'];

  private readonly weekdaysShort = ['day.mon', 'day.tue', 'day.wed', 'day.thur', 'day.fri', 'day.sat', 'day.sun'];

  /**
   * Format date with localization It used to be named "formatDate", but it threw 'too much recursion'
   * @param value    Date-like value or null
   * @param {DateFormat} format
   * @param {string}     empty    (optional) string when date is null
   * @param {string}     timezone (optional) timezone of date
   * @return {string} formatted and localized date
   */
  public localizeDate(
    value: Date | number | string | undefined | null,
    format: DateFormat,
    empty?: string,
    timezone?: string
  ): string {
    let f = '';
    // TODO by @Aandrzejmi: handling separators, and other deviations in different languages
    // prettier-ignore
    switch (format) {
      case 'dShort':        f = 'dd.MM.yyyy';                 break;
      case 'dMed':          f = 'dd MMM yyyy';                break;
      case 'dLong':         f = 'dd MMMM yyyy';               break;
      case 'dLongDay':      f = 'EEEE, dd MMMM yyyy';         break;
      case 'dMonthLong':    f = 'MMMM';                       break;
      case 'dMonthMed':     f = 'MMM';                        break;
      case 'dShortNoYear':  f = 'dd.MM';                      break;
      case 'dMedNoYear':    f = 'dd MMM';                     break;
      case 'dLongNoDay':    f = 'LLLL yyyy';                  break;
      case 'tShort':        f = 'HH:mm';                      break;
      case 'tLong':         f = 'HH:mm:ss';                   break;
      case 'dtShort':       f = 'dd.MM.yyyy HH:mm';           break;
      case 'dtShortNoYear': f = 'dd.MM HH:mm';                break;
      case 'dtMed':         f = 'dd MMM yyyy, HH:mm';         break;
      case 'dtMedNoYear':   f = 'dd MMM, HH:mm';              break;
      case 'dtLong':        f = 'dd MMMM yyyy, HH:mm';        break;
      case 'file':          f = 'dd-MM-yyyy_HH-mm-ss-SSSS';   break;
      case 'dISO':          f = 'yyyy-MM-dd';                 break;
    }
    return value ? formatDate(value, f, this.ls.currentLocale, timezone) : (empty ?? '');
  }

  public dayHR(index: number): string {
    if (index != -1) {
      return this.getDayName(index, true);
    } else {
      return this.ls.translate('label.everyday');
    }
  }

  /**
   * Get day name from number
   * @param {number}  dayNumber day index; 0 is monday, 6 is saturday
   * @param {boolean} longName  if it should be long number
   * @return {string} day name in language
   */
  public getDayName(dayNumber: number, longName: boolean = false): string {
    const tab = longName ? this.weekdaysLong : this.weekdaysShort;
    return this.ls.translate(tab[dayNumber]);
  }

  /**
   * Get formatted date to range
   * @param {dTime}   from
   * @param {dTime}   to
   * @param {boolean} noDay don't display day when same day
   * @return {string} Range dates in format:
   *                   Same Day            -> HH:mm        - HH:mm dd MMMM yyyy;
   *                   Same Month and Year -> dd           -       dd MMMM yyyy;
   *                   Same Year           -> dd MMMM      -       dd MMMM yyyy;
   * @prettierignore   Other               -> dd MMMM yyyy -       dd MMMM yyyy;
   */
  public getRangedDate(from: dTime, to: dTime, noDay: boolean = false, allDay: boolean = false): string {
    let ret = '';
    const start = new Date(from);
    const end = new Date(to);
    if (start.getDate() === end.getDate() && start.getMonth() == end.getMonth() && start.getFullYear() == end.getFullYear()) {
      //* same day - hours
      ret =
        (noDay ? '' : this.localizeDate(start, 'dLong') + ', ') +
        `${this.localizeDate(start, 'tShort')} - ${this.localizeDate(end, 'tShort')}`;
      // empty line for better reading
    } else if (start.getMonth() == end.getMonth() && start.getFullYear() == end.getFullYear()) {
      if (allDay) {
        //* same month and year
        ret = `${start.getDate()} - ${end.getDate()} ${this.localizeDate(start, 'dMonthLong')} ${start.getFullYear()}`;
      } else ret = `${this.localizeDate(start, 'dtMed')} - ${this.localizeDate(end, 'dtMed')}`;
    } else if (start.getFullYear() == end.getFullYear()) {
      //* same year but different moths
      ret =
        `${start.getDate()} ${this.localizeDate(start, 'dMonthLong')} - ${end.getDate()} ` +
        `${this.localizeDate(end, 'dMonthLong')} ${start.getFullYear()}`;
      // empty line for better reading
    } else {
      //* different years
      ret = `${this.localizeDate(start, 'dLong')} - ${this.localizeDate(end, 'dLong')}`;
    }
    return ret;
  }

  /**
   * Return time length in human readable form e.g. 3 days 12 hours
   * @param {number} value  time length in milliseconds
   * @param {string} format w - week, d - days, h - hours, m - minutes; big letters: force 0; default: 'dhm'
   */
  public getTimeHR(value: number, format = 'dhm'): string {
    const elements: string[] = [];

    for (const char of format) {
      const formatParam = this.getTimeHrLookup.get(char as any);

      if (!formatParam) throw new Error(`Invalid format pattern: ${format} - unknown character: '${char}'`);

      const [amount, _value] = MathHlp.divRem(value, TimeHlp.ms[formatParam.unit]);
      value = _value;
      if (amount || formatParam.forced) elements.push(this.translateTime(amount, formatParam.unit));
    }
    return elements.join(' ');
  }

  private readonly getTimeHrLookup = new Map([
    ['w', { unit: 'WEEK' }],
    ['W', { unit: 'WEEK', forced: true }],
    ['d', { unit: 'DAY' }],
    ['D', { unit: 'DAY', forced: true }],
    ['h', { unit: 'HOUR' }],
    ['H', { unit: 'HOUR', forced: true }],
    ['m', { unit: 'MINUTE' }],
    ['M', { unit: 'MINUTE', forced: true }]
  ] as const);

  /**
   * Returns human readable form of given time units e.g. 4 hours or 3.4 seconds
   * @param {number}                             value
   * @param {'WEEK' | 'MINUTE' | 'HOUR' | 'DAY'} unit
   * @return {string}
   */
  public translateTime(value: number, unit: 'WEEK' | 'DAY' | 'HOUR' | 'MINUTE'): string {
    let key = `label.${unit.toLowerCase()}`;
    key = Number.isInteger(value) ? GrammarHelper.getCounterKey(key, value, null) : key + 's';
    return `${this.formatNumber(value, 'oneDec')} ${this.ls.translate(key)}`;
  }

  /**
   * Get time duration human readable
   * @param {Date | number} start
   * @param {Date | number} end
   * @param {boolean}       onlyDays show only days in duration (omit hours and minutes)
   * @return {string} time duration HR, eg. 4 days, 2 days 1 hour 37 minutes etc.
   */
  public getDurationHR(start: Date | number, end: Date | number, onlyDays = false) {
    // TODO: reimplement using translateTime()
    const startNumber = typeof start == 'number' ? start : start.valueOf();
    const endNumber = typeof end == 'number' ? end : end.valueOf();

    const duration = (num: number, key: string) => `${num} ${this.ls.translate(GrammarHelper.getCounterKey(key, num, null))}`;

    const time = endNumber - startNumber;
    let ret = '';
    const days = Math.floor(time / TimeHlp.ms.DAY);
    if (days > 0) ret += `${days} ${this.ls.translate('label.day' + (days == 1 ? '' : 's'))} `;
    if (!onlyDays) {
      const hours = Math.floor(time / (1000 * 3600) - days * 24);
      ret += duration(hours, 'label.hour');
      const minutes = Math.floor(time / (1000 * 60) - (days * 24 + hours) * 60);
      ret += duration(minutes, 'label.minute');
    }
    return ret;
  }

  /**
   * Show minutes in XX h YY m(in) format
   * @param {number}  value     number of minutes to parse
   * @param {boolean} underHour show YY m(in) if amount of minutes is under hour (sometimes it's not necessary)
   * @param {boolean} short     flag that indicates shorter version of time labels; short: h, m, long: h, min
   * @return {string} Amount of hours and minutes in parsed time in readable form
   */
  public minutesToHours(value: number, underHour: boolean = false, short: boolean = true): string {
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    const hourString = hours >= 1 ? `${hours}${short ? 'h' : ' ' + this.ls.translate('label.hrs')}` : '';
    const minuteString = (hours < 1 && underHour) || (hours >= 1 && minutes > 1) ? `${minutes}${short ? 'm' : ' min'}` : '';
    return `${hourString}${hourString && minuteString ? ' ' : ''}${minuteString}`;
  }

  /**
   * Return human-readable description of relative date, like: "tomorrow" or "15 days ago"
   * @param {number} day
   * @return {string}
   */
  public relativeDays(day: number): string {
    switch (day) {
      case 2:
        return this.ls.translate('label.inDays2');
      case 1:
        return this.ls.translate('label.tomorrow');
      case 0:
        return this.ls.translate('label.today');
      case -1:
        return this.ls.translate('label.yesterday');
      case -2:
        return this.ls.translate('label.agoDays2');
      default:
        return this.ifElse(day > 0, 'label', 'inDays', 'agoDays', { $n: Math.abs(day) });
    }
  }

  /**
   * Return human-readable description of date/time, like: "tomorrow" or "15 minutes ago"
   * @param {number} date
   * @return {string}
   */
  public relativeTime(date: tTime): string {
    const diff = TimeHlp.timestamp(date) - Date.now();
    const abs = Math.abs(diff);

    if (abs > TimeHlp.ms.DAY) {
      return this.relativeDays(MathHlp.signFloor(diff / TimeHlp.ms.DAY));
    }

    let base = diff > 0 ? 'label.in' : 'label.ago';
    let $n = 0;

    if (abs > TimeHlp.ms.HOUR) {
      base += 'Hour';
      $n = Math.floor(abs / TimeHlp.ms.HOUR);
    } else if (abs > TimeHlp.ms.MINUTE) {
      base += 'Minute';
      $n = Math.round(abs / TimeHlp.ms.MINUTE);
    } else {
      base += 'Second';
      $n = Math.round(abs / TimeHlp.ms.SECOND);
    }

    return this.ls.trReplace(GrammarHelper.getCounterKey(base, $n, ''), { $n });
  }

  // #endregion

  // #region Numbers
  /**
   * Display share as `percentage% [share value]`
   * @param {number} share   numerical share value
   * @param {number} percent percent share value
   * @return {string}
   */
  public sharePercentage(share: number, percent: number): string {
    return `${this.formatNumber(percent, 'twoDec')}% [${this.formatNumber(share, 'twoDec')}]`;
  }

  /**
   * Count share percentage of total and display as `percentage% [share value]`
   * @param {number} share
   * @param {number} total
   * @return {string}
   */
  public shareOfTotal(share: number, total: number): string {
    return this.sharePercentage(share, (share / total) * 100);
  }

  /**
   * Parse and format number to given format
   * @param {number | string | undefined | null} value
   * @param {NumberFormat}                       format
   * @return {string}
   */
  public formatNumber(value: number | string | undefined | null, format: NumberFormat): string {
    let f = '';
    // prettier-ignore
    switch (format) {
      case 'twoDec':      f = '1.0-2';  break;
      case 'oneDec':      f = '1.0-1';  break;
      case 'noDec':       f = '1.0-0';  break;
      case 'twoDecConst': f = '1.2-2';  break;
    }

    let num = 0;
    if (value) num = typeof value == 'string' ? ParserHlp.parseFloat(value) : value;
    return formatNumber(num, this.ls.currentLocale ?? 'pl', f);
  }
  // #endregion

  // #region Defects
  /**
   * Get defectType's label in current language OR first available OR id
   * @param type
   * @return
   */
  public defectTypeLabel(type: DefectType | ConciergeType | null | undefined): string {
    return type
      ? type.label[this.ls.currentLang as Language] || Object.values(type.label)[0] || type.id
      : this.ls.translate('label.nd');
  }

  /**
   * Get human readable value of DefectLocationCommon enum
   * @param {DefectLocationCommon} type
   * @return {string}
   */
  public locationCommonHR(type: DefectLocationCommon): string {
    let key = '';
    switch (type) {
      case DefectLocationCommon.BUILDING:
        key = 'defect.locCommon';
        break;
      case DefectLocationCommon.OUTDOOR:
        key = 'defect.outside';
        break;
    }
    return this.ls.translate(key);
  }

  /**
   * Get human readable value of Payer enum
   * @param {Payer | string | null | undefined} payer
   * @param {string}                            empty displayed value if payer is null
   * @return {string}
   */
  public payerHR(payer: Payer | string | null | undefined, empty: string = '-'): string {
    let key = '';
    switch (payer) {
      case Payer.RESIDENT:
        key = 'PRSlandlord.nominative';
        break;
      case Payer.ESTATE:
        key = 'defect.tenant';
        break;
      default:
        return empty;
    }
    return this.ls.translate(key);
  }

  /**
   * Get human readable value of IssueStatus enum
   * @param {IssueStatus | string} enumValue
   * @param {boolean}              isReport  flag to check if it's for report (different keys and translations)
   * @return {string}
   */
  public defectTypeHR(enumValue: IssueStatus | string, isReport = false): string {
    let key = isReport ? 'rDefectType.' : 'defectType.';
    key += enumValue?.toString() || 'noticed';
    return this.ls.translate(key);
  }

  /**
   * Get human readable value of IssueFieldChangeKey enum
   * @param {IssueFieldChangeKey} field
   * @param {IssueType}           type  used differentiate keys for different issue types
   * @return {string}
   */
  public historyElementHR(field: IssueFieldChangeKey, type: IssueType): string {
    let key = '';
    // prettier-ignore
    switch (field) {
      case 'locationInside':          key = 'label.location';       break;
      case 'locationBuilding':        key = 'addr.blockNo';         break;
      case 'locationStairway':        key = 'addr.stairNo';         break;
      case 'locationFloor':           key = 'addr.floor';           break;
      case 'locationApartment':       key = 'addr.number';          break;
      case 'status':                  key = 'label.status';         break;
      case 'title':                   key =  type + '.title';       break;
      case 'description':             key =  type + '.desc';        break;
      case 'assignedSubcontractorId': key = 'label.subcontractor';  break;
      case 'assignedServiceUserId':   key = 'defect.serviceUser';   break;
      case 'assignedUserId':          key = 'defect.assignedUser';  break;
      case 'payer':                   key = 'defect.payer';         break;
      case 'finalPricing':            key = 'defect.payment';       break;
      case 'estimatedPricing':        key = 'defect.paymentEst';    break;
      case 'typeId':                  key = 'defect.type';          break;
      case 'reporterViewOnly':        key = 'tooltip.mobile.show';  break;
      case 'locationUnitId':          key = 'label.unit';           break;
      case 'locationUnitType':        key = 'defect.unitType';      break;
      case 'sendToWarranty':          key = 'defect.warranty';      break;
      case 'estimatedRepairDate':     key = 'defect.repairDateEst'; break;
      case 'closeDate':               key = 'defect.repairDate';    break;
      case 'priority':                key = 'label.priorityShort';  break;
      case 'rating':                  key = 'label.rating';         break;
      default:
        return field;
    }
    return StrHlp.capitalize(this.ls.translate(key));
  }

  // #endregion
}
